import React, { useState, useEffect } from 'react';
import { get } from '../api/client';

interface VersionInfo {
  version: string;
  download_url: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'What is AirText?',
    answer: 'Type on your phone, text appears at your Windows cursor instantly without any copy-pasting or file transfers.',
  },
  {
    question: 'Is it secure?',
    answer: 'End-to-end encrypted via TLS, no server-side storage. Your text passes through an in-memory relay directly to your PC.',
  },
  {
    question: 'Do I need an account?',
    answer: 'No accounts, no sign-up, no tracking. Just launch the app on Windows, scan the QR code with your mobile camera, and start typing.',
  },
  {
    question: 'Which Windows versions are supported?',
    answer: 'Windows 10 (1903+) and Windows 11, x64 architecture.',
  },
  {
    question: 'What is WebView2?',
    answer: 'Required runtime that ships with Windows 11 and most Windows 10 devices. It powers the lightweight desktop user interface.',
  },
];

export const Landing: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);

  useEffect(() => {
    get<VersionInfo>('/api/version')
      .then(data => setVersionInfo(data))
      .catch(() => {});
  }, []);

  const downloadUrl = versionInfo?.download_url || 'https://github.com/gityasin/airtext/releases/latest';

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans selection:bg-indigo-500 selection:text-white">
      {/* Header / Nav */}
      <header className="border-b border-slate-800/80 bg-slate-950/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center font-bold text-white shadow-lg shadow-indigo-500/20">
              A
            </div>
            <span className="text-xl font-bold tracking-tight bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
              AirText
            </span>
          </div>
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors shadow-md shadow-indigo-600/20"
          >
            Download for Windows
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto text-center flex flex-col items-center">
        <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-medium mb-8">
          <span>Windows-First Desktop Typing</span>
        </div>
        <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-white max-w-4xl leading-tight sm:leading-tight">
          Type from your phone to your{' '}
          <span className="bg-gradient-to-r from-indigo-400 via-indigo-300 to-sky-400 bg-clip-text text-transparent">
            Windows cursor
          </span>
        </h1>
        <p className="mt-6 text-lg sm:text-xl text-slate-400 max-w-2xl font-normal leading-relaxed">
          Instantly bridge your phone’s keyboard with your Windows desktop. Zero accounts, end-to-end TLS security, and no persistent server storage.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-indigo-600 hover:bg-indigo-500 rounded-xl transition-all shadow-xl shadow-indigo-600/25 hover:shadow-indigo-600/35"
          >
            <svg
              className="w-5 h-5 mr-2.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Download for Windows
          </a>
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Supports Windows 10 (1903+) &amp; Windows 11 (x64)
        </p>
      </section>

      {/* How It Works Section */}
      <section className="py-16 sm:py-24 border-t border-slate-800/60 bg-slate-900/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white tracking-tight">
              How It Works
            </h2>
            <p className="mt-3 text-slate-400">
              Get started in seconds with three simple steps
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-900/80 border border-slate-800 p-8 rounded-2xl relative flex flex-col items-start">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 font-bold text-xl flex items-center justify-center mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Install the Windows app
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Download and launch the lightweight desktop utility on your Windows PC.
              </p>
            </div>

            <div className="bg-slate-900/80 border border-slate-800 p-8 rounded-2xl relative flex flex-col items-start">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 font-bold text-xl flex items-center justify-center mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Scan the QR code with your phone
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Open your mobile browser or camera and scan the dynamic QR code displayed on desktop.
              </p>
            </div>

            <div className="bg-slate-900/80 border border-slate-800 p-8 rounded-2xl relative flex flex-col items-start">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 font-bold text-xl flex items-center justify-center mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Start typing
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Use your phone’s keyboard to type text directly into whichever app has focus on Windows.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Promise Section */}
      <section className="py-16 sm:py-24 border-t border-slate-800/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-white tracking-tight">
            Privacy Promise
          </h2>
          <p className="mt-4 text-slate-300 text-lg leading-relaxed">
            AirText is designed around total privacy. No accounts, no sign-up, and no server storage of text.
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
            <div className="bg-slate-900/60 border border-slate-800 p-6 rounded-xl">
              <h4 className="font-semibold text-white mb-1">No Accounts</h4>
              <p className="text-xs text-slate-400">Zero sign-up or email required. Connect instantly using QR pairing.</p>
            </div>
            <div className="bg-slate-900/60 border border-slate-800 p-6 rounded-xl">
              <h4 className="font-semibold text-white mb-1">No Sign-Up</h4>
              <p className="text-xs text-slate-400">No passwords or personal details to manage or leak.</p>
            </div>
            <div className="bg-slate-900/60 border border-slate-800 p-6 rounded-xl">
              <h4 className="font-semibold text-white mb-1">No Server Storage</h4>
              <p className="text-xs text-slate-400">Your typed content is bridged directly and never stored on disk.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Security Model Section */}
      <section className="py-16 sm:py-24 border-t border-slate-800/60 bg-slate-900/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white tracking-tight">
              Security Model
            </h2>
            <p className="mt-3 text-slate-400">
              Engineered with ephemeral sessions and modern encryption
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl">
              <h3 className="text-lg font-semibold text-white mb-2">TLS Encryption</h3>
              <p className="text-slate-400 text-sm">
                All communications between mobile browser, relay, and desktop app are encrypted in transit over TLS.
              </p>
            </div>
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl">
              <h3 className="text-lg font-semibold text-white mb-2">No Persistent Storage</h3>
              <p className="text-slate-400 text-sm">
                The relay operates purely in-memory. Tokens are ephemeral and expire automatically.
              </p>
            </div>
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl">
              <h3 className="text-lg font-semibold text-white mb-2">Ephemeral Sessions</h3>
              <p className="text-slate-400 text-sm">
                Sessions terminate instantly when disconnected, leaving no trace behind.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* System Requirements Section */}
      <section className="py-16 border-t border-slate-800/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold text-white">System Requirements</h3>
              <p className="text-slate-400 text-sm mt-1">
                Everything you need to run AirText on your desktop:
              </p>
              <ul className="mt-4 space-y-2 text-sm text-slate-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 rounded-full bg-indigo-400 mr-2.5"></span>
                  Windows 10 (version 1903+) or Windows 11 (x64)
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 rounded-full bg-indigo-400 mr-2.5"></span>
                  WebView2 Runtime (pre-installed on Windows 11 &amp; most Windows 10 PCs)
                </li>
              </ul>
            </div>
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap px-6 py-3 text-sm font-semibold text-slate-200 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors border border-slate-700"
            >
              Download Windows App
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section className="py-16 sm:py-24 border-t border-slate-800/60 bg-slate-900/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="mt-3 text-slate-400">
              Have questions? We&apos;ve got answers.
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden transition-colors"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between font-semibold text-white focus:outline-none focus:bg-slate-800/50"
                  aria-expanded={openFaq === index}
                >
                  <span>{faq.question}</span>
                  <svg
                    className={`w-5 h-5 text-indigo-400 transition-transform duration-200 ${
                      openFaq === index ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-slate-400 text-sm leading-relaxed border-t border-slate-800/50 pt-3">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-auto border-t border-slate-800 py-8 bg-slate-950 text-slate-500 text-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 rounded bg-indigo-600 flex items-center justify-center text-white text-xs font-bold">
              A
            </div>
            <span className="font-semibold text-slate-300">AirText{versionInfo ? ' v' + versionInfo.version : ''}</span>
          </div>
          <div>&copy; 2026 AirText. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
